/**
 * FORME PARALLÈLE de la compréhension de l'écrit en 4e (11/10/2026), support documentaire : « Volcans sous la mer ».
 * En septembre, les élèves ont lu « Volcans sous la glace » (évaluation nationale) ; le QCM de suivi leur donne un
 * autre document du même genre, avec ses propres questions, réparties dans les mêmes compétences (2 « explicite »,
 * 4 « inference », 5 « sens_global », comme les onze items de septembre) et aux mêmes formats (onze QCM, comme
 * septembre). La progression se lit par compétence et par support, jamais question par question. Il n'existe pas
 * d'outil de positionnement du ministère en 4e : le document est celui des variantes (2026/francais/4e/volcans.js),
 * réécrit d'après l'audit externe du 11/10 (EvalNatClgBedier/audit_variantes_francais_2026-10-11.md).
 *
 * Ce qui change par rapport au texte des variantes :
 *   - la difficulté : l'audit mesurait 57 (variantes) contre 42 (septembre) à l'indice de Kandel-Moles
 *     (207 - 1,015 x mots par phrase - 73,6 x syllabes par mot ; plus il est haut, plus le texte est facile).
 *     Mêmes règles sur les trois textes (mots séparés par les espaces, un nombre lu en toutes lettres) :
 *       syllabes orales (e muet final non compté) : variantes 68,5 ; septembre 54,4 ; ce texte 54,6 ;
 *       syllabes écrites (e muet final compté)     : variantes 46,6 ; septembre 33,9 ; ce texte 32,5.
 *     Phrases de 23,6 mots en moyenne (septembre 23,0 ; variantes 19,7), lexique savant expliqué par le contexte
 *     (plaines abyssales, hydrophones, pierres ponces, photosynthèse, paroxysme) ; 851 mots (septembre 783) ;
 *   - les faits : « plus d'une fois et demie le tour de la Terre » pour 65 000 km (le tour de la Terre fait
 *     environ 40 000 km), et l'éruption du Hunga Tonga commencée fin décembre 2021, avec son paroxysme le
 *     15 janvier 2022 ; chaque fait chiffré revérifié (SOURCES plus bas) ;
 *   - l'orthographe rectifiée, comme le ministère (chaine, iles, brulantes, connait), apostrophes typographiques,
 *     espaces insécables (typo), jamais de tiret cadratin ; le schéma de la page 3 passe de « brûlante » à
 *     « brulante » ;
 *   - les dessins de volcans.js (DESSINS_VOLCANS), sous les pages qui en parlent, source « Illustration TanoEd ».
 *
 * Les questions ne reprennent aucun item de septembre, ni sa tâche, ni sa clé, ni sa grille (septembre : Q01 le
 * type de publication, Q02 l'image qui explique une éruption, Q03 pourquoi la région réserve des surprises,
 * Q04 l'information illustrée par la carte, Q05 la chaine la plus … du monde, Q06 l'information donnée par le
 * schéma, Q07 pourquoi un « monde perdu », Q08 l'idée principale de la page 5, Q09 la citation illustrée par
 * l'image, Q10 la phrase qui résume le document, Q11 la partie utile pour un exposé). Aucune question ne donne la
 * réponse d'une autre (relu choix par choix, voir chaque fiche). Les clés des QCM sont réparties de A à D.
 * Numéros 11 à 21 dans la forme : ils suivent les dix questions du texte littéraire de la même forme.
 *
 * Relectures externes à l'aveugle du 11/10 (deux relecteurs) : onze clés retrouvées sur onze, aucune réponse
 * défendable de plus, aucun fait faux. Corrigé ensuite : les fuites de Q08 vers Q07 et de Q06 vers Q02, la bonne
 * réponse trop souvent la plus longue ou la seule générale (Q01, Q02, Q04, Q05, Q08, Q09), Q07 en QCM (septembre
 * n'avait que des QCM : un tableau noté en tout ou rien aurait faussé le taux des explicites), Q10 en ordre des
 * parties, le sous-titre du schéma (il donnait la réponse de Q04), l'expédition de 1977 (venue « chercher » une
 * source, et non l'étudier), trois tournures. Reste, en connaissance de cause : le plan du document suit celui de
 * septembre ; Q10 se lit par les titres des pages, comme la Q11 de septembre.
 *
 * Les questions sont créées en base par un script du back, d'après ce module ; le texte reste ici, référencé par
 * support_lecture ({ id, titre }).
 */
import { typo, qcm as qcmForme, question as questionForme } from './fabrique';
import { DESSINS_VOLCANS } from '../2026/francais/4e/volcans';

/** Le second chemin : une construction fausse lève une erreur au lieu de partir (REGLES_EQUIPE § 3). */
const garde = (condition, message) => {
    if (!condition) throw new Error(`Forme parallèle 4e, Volcans sous la mer : ${message}`);
};

/*
 * ─── SOURCES DU DOCUMENT (vérifiées le 11/10/2026) ───────────────────────────────────────────────────
 * Page 1 : les océans couvrent environ 70 % de la surface de la Terre (NOAA, Ocean Today ; 71 % ailleurs) ; la
 *   pression augmente d'environ une atmosphère tous les 10 m, donc près de 400 fois celle de la surface à
 *   4 000 m (NOAA Ocean Exploration et Ocean Service, « How does pressure change with ocean depth? ») ; eau des
 *   grands fonds proche de 2 °C (WHOI, « 1977, Astounding Discoveries ») ; des dizaines de milliers de volcans :
 *   43 454 monts sous-marins catalogués en 2023 (Gevorgian et al., Earth and Space Science), la plupart d'anciens
 *   volcans éteints, certains encore actifs (NOAA Ocean Exploration, « What is a seamount? »).
 * Page 2 : dorsale de près de 65 000 km, sommet en moyenne à 2 500 m de profondeur, éruptions de basalte
 *   (NOAA Ocean Exploration, « What is the mid-ocean ridge? ») ; 65 000 km = 1,6 tour de la Terre (40 075 km) ;
 *   plus longue que toute chaine des continents (les Andes, la plus longue : 7 000 à 8 900 km selon les
 *   sources ; Guinness, Wikipédia) ; elle domine les plaines abyssales d'environ 2 à 3 km (Wikipédia,
 *   « Mid-ocean ridge » : environ 2 000 m ; Oberlin College : 2,5 à 4 km), d'où « de plusieurs kilomètres » ;
 *   moins d'un tiers des fonds cartographiés finement (Nippon Foundation-GEBCO Seabed 2030 : 27,3 % en juin
 *   2025, 28,7 % en avril 2026) ; 19 325 monts sous-marins inconnus repérés en 2023 par altimétrie satellitaire,
 *   la gravité du mont faisant bomber la surface de la mer au-dessus de lui (Gevorgian et al., 2023 ; SOEST,
 *   université d'Hawaï, « More than 19,000 undersea volcanoes discovered » ; NOAA STAR, Laboratory for Satellite
 *   Altimetry : une bosse de quelques mètres au plus, étalée sur des dizaines de kilomètres).
 * Page 3 : éruptions loin des côtes, détectées par les sons de la croûte qui se fracture et de la lave, captés
 *   par des hydrophones à des milliers de kilomètres (réseau AHA de la NOAA, plus de 5 400 km ; WHOI, Dive and
 *   Discover) ; eau teintée et radeaux de pierre ponce, roche si poreuse qu'elle flotte (NASA Earth Observatory,
 *   « A Raft of Rock », 2019, et suivants) ; surveillance par satellites et robots.
 * Page 4 : eau de mer infiltrée, chauffée en profondeur, ressortie chargée de minéraux (NOAA Ocean Service,
 *   « What is a hydrothermal vent? ») ; 1977, rift des Galápagos : géologues, géochimistes et géophysiciens venus
 *   chercher des sources repérées l'année d'avant, « personne n'avait imaginé avoir besoin d'un biologiste à
 *   bord », sous-marin Alvin (trois personnes), caméra à 2 500 m, palourdes blanches jusqu'à 30 cm, moules,
 *   crabes blancs, vers à tige blanche et sommet rouge (WHOI, « 1977, Astounding Discoveries ») ; fumeurs noirs
 *   à 350 °C, 1979, dorsale du Pacifique Est (WHOI, « 1979, The Smoking Gun » ; projet RISE) ; chimiosynthèse,
 *   ver géant sans bouche ni tube digestif nourri par ses bactéries (Animal Diversity Web, Riftia pachyptila ;
 *   WHOI Oceanus) ; des centaines d'espèces nouvelles depuis 1977 (WHOI) ; escargot au pied couvert d'écailles
 *   de sulfure de fer (Warén et al., Science, 2003 ; JAMSTEC) ; vers et escargots dans des cavités sous le
 *   plancher, expédition de 2023 (Schmidt Ocean Institute, août 2023 ; Bright et al., Nature Communications,
 *   2024).
 * Page 5 : Hunga Tonga-Hunga Ha'apai, volcan en grande partie sous-marin ; éruption commencée le 20 décembre
 *   2021, paroxysme le 15 janvier 2022 (Smithsonian, Global Volcanism Program, bulletins de février et mars
 *   2022) ; entendue en Alaska, à près de 6 000 milles, soit 9 650 km (Anchorage Daily News, 15 janvier 2022) ;
 *   panache à 57 km, le plus haut jamais mesuré (Proud et al., Science, 2022 ; université d'Oxford) ; tsunami :
 *   maisons et villages détruits dans l'archipel, vagues jusqu'au Japon et aux Amériques (Croix-Rouge, OCHA,
 *   Caritas) ; cendres : réserves d'eau contaminées, cultures abimées (OCHA ; NASA Earth Observatory, « Island
 *   Nation Hit Hard by Eruption ») ; câble Internet rétabli plus de cinq semaines après (Associated Press,
 *   22 février 2022) ; dégâts : 90,4 millions de dollars, 18,5 % du PIB (Banque mondiale, 14 février 2022).
 */

// ─── Le document ───────────────────────────────────────────────────────────────────────────────────

const NB = String.fromCharCode(0xa0);
const enImage = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;

// Le schéma de la page 3 en orthographe rectifiée (le dessin d'origine écrit « brûlante »), et sans « près de la
// surface » dans son sous-titre : le dessin le montre, l'élève doit le lire (Q04, relecture externe du 11/10)
const SOUS_TITRE_SCHEMA = 'Le cratère du volcan se trouve sous la mer, près de la surface.';
const SCHEMA = DESSINS_VOLCANS.schema.split('brûlante').join('brulante')
    .split(SOUS_TITRE_SCHEMA).join('Le cratère du volcan se trouve sous la mer.');
const { globe: GLOBE, carte: CARTE, vers: VERS, crabe: CRABE, panache: PANACHE } = DESSINS_VOLCANS;

const SOURCE_IMAGE = typo('Source de l’image : Illustration TanoEd');
const ALT = {
    globe: 'Globe terrestre vu du côté de l’océan Pacifique',
    carte: typo('Carte du monde : la dorsale océanique'),
    schema: 'Schéma de principe d’une éruption sous-marine',
    vers: 'Dessin de vers géants à panache rouge, près d’une cheminée',
    crabe: 'Dessin d’un crabe blanc, de palourdes et de moules',
    panache: 'L’éruption vue de loin, au-dessus de l’océan Pacifique',
};

/**
 * Le texte : 5 pages, une partie titrée par page, comme le document de septembre ; texte brut, une ligne vide
 * entre les paragraphes. Environ 850 mots par pages de 79, 180, 123, 276 et 193 (septembre : 783 mots, par pages
 * de 57, 175, 112, 264 et 175, comptés de la même façon).
 */
export const SUPPORT_VOLCANS = {
    id: 'forme-4e-volcans-sous-la-mer',
    titre: 'Volcans sous la mer',
    pages: [
        {
            page: 1,
            surtitre: typo('Texte documentaire : « Volcans sous la mer »'),
            title: 'Des volcans sous la mer',
            content: typo(`Les océans recouvrent plus des deux tiers de la surface terrestre, et pourtant leurs profondeurs figurent toujours parmi les milieux les moins explorés de la planète. L’obscurité totale, un froid permanent et surtout une pression colossale, près de 400 fois plus forte à 4 000 mètres de profondeur qu’à la surface, y rendent chaque expédition particulièrement périlleuse.

Les scientifiques savent néanmoins que, sous ces kilomètres d’eau glaciale, se dissimulent des dizaines de milliers de volcans, dont certains demeurent en activité.`),
            images: [{ src: enImage(GLOBE), alt: ALT.globe }],
            source: SOURCE_IMAGE,
        },
        {
            page: 2,
            title: 'Une immense chaine de montagnes sous-marine',
            content: typo(`Au fond des océans serpente une gigantesque chaine de montagnes volcaniques, que les géologues appellent la dorsale océanique. Longue d’environ 65 000 kilomètres, soit plus d’une fois et demie le tour de la Terre, elle surpasse considérablement toutes les chaines des continents. Ses sommets dominent de plusieurs kilomètres les plaines abyssales, ces immenses étendues plates qui tapissent le fond des océans, mais se trouvent encore, en moyenne, à 2 500 mètres sous la surface.

Les fonds marins restent d’ailleurs largement méconnus : moins d’un tiers de leur surface a été cartographié avec précision. En 2023, une équipe de chercheurs a cependant recensé plus de 19 000 volcans sous-marins jusque-là inconnus, sans qu’aucun engin ne descende les observer. Des satellites mesurent en effet avec une extrême précision la hauteur de la surface de l’océan. Or la masse d’un volcan sous-marin attire légèrement l’eau qui le recouvre, si bien qu’à sa verticale, la mer forme un léger renflement, invisible à l’œil nu. La plupart de ces volcans sont aujourd’hui éteints ; le long de la dorsale, en revanche, le magma continue de remonter et provoque encore des éruptions.`),
            images: [{ src: enImage(CARTE), alt: ALT.carte }],
            source: SOURCE_IMAGE,
        },
        {
            page: 3,
            title: 'Des éruptions difficiles à observer',
            content: typo(`Les volcanologues connaissent depuis longtemps l’existence d’éruptions sous-marines. La plupart de ces éruptions échappent pourtant à toute observation directe, car elles se déroulent loin des côtes, sous des centaines, voire des milliers de mètres d’eau.

Pour les détecter, les scientifiques doivent donc se fier à des indices. Ils guettent ainsi une série de petits séismes ou des grondements sourds, enregistrés par des hydrophones, ces microphones immergés capables de capter des sons à des milliers de kilomètres. Une eau soudainement teintée ou des radeaux de pierres ponces, ces roches volcaniques si poreuses qu’elles flottent, peuvent également signaler une éruption. Afin de ne rien laisser échapper, les chercheurs combinent désormais plusieurs outils de surveillance complémentaires : des réseaux d’hydrophones, des images satellitaires et des robots sous-marins.`),
            images: [{ src: enImage(SCHEMA), alt: ALT.schema }],
            source: SOURCE_IMAGE,
        },
        {
            page: 4,
            title: 'Un écosystème autour des sources chaudes',
            content: typo(`Le long de la dorsale, l’eau de mer s’infiltre dans les fissures du plancher océanique, se réchauffe au contact de roches brulantes, puis remonte chargée de minéraux dissous : ce sont les sources hydrothermales. En 1977, une expédition de géologues, de géochimistes et de géophysiciens venait précisément en chercher une, près des iles Galápagos. Lorsque le petit sous-marin Alvin atteignit le fond, à environ 2 500 mètres de profondeur, ses passagers découvrirent, stupéfaits, une véritable « oasis » grouillant de vie. L’équipe ne comptait pourtant aucun biologiste.

À de telles profondeurs, la vie est habituellement très rare : faute de lumière, aucune plante ne peut y réaliser la photosynthèse, ce processus qui transforme l’énergie du Soleil en matière vivante et dont dépendent presque toutes les chaines alimentaires. Autour des sources, en revanche, des bactéries puisent leur énergie dans les substances chimiques dissoutes dans l’eau chaude et fabriquent ainsi la matière organique dont se nourrissent de nombreux animaux. Ailleurs sur la dorsale, l’eau jaillit même de hautes cheminées à plus de 350${NB}°C, mais elle se mélange aussitôt à l’eau environnante, proche de 2${NB}°C : les animaux prospèrent entre ces deux extrêmes.

Les chercheurs ont ainsi observé des palourdes blanches atteignant 30 centimètres, des moules, des crabes blancs et des vers géants à panache rouge, abrités dans de longs tubes blancs. Dépourvu de bouche et d’intestin, le ver géant héberge dans son organisme d’innombrables bactéries qui le nourrissent. Depuis, des centaines d’espèces nouvelles ont été décrites autour des sources hydrothermales, comme un escargot dont le pied est recouvert d’écailles contenant du fer. En 2023, une expédition a même découvert des vers et des escargots vivant dans des cavités situées sous le plancher océanique.`),
            images: [
                { src: enImage(VERS), alt: ALT.vers },
                { src: enImage(CRABE), alt: ALT.crabe },
            ],
            legende: 'Quelques habitants des sources hydrothermales (dessins).',
            source: typo('Source des images : Illustration TanoEd'),
        },
        {
            page: 5,
            title: 'L’éruption d’un volcan sous-marin dans le Pacifique',
            content: typo(`Le Hunga Tonga-Hunga Ha’apai, un volcan presque entièrement immergé, se dresse dans l’archipel des Tonga, dans le sud de l’océan Pacifique. Entré en éruption à la fin de décembre 2021, il atteint son paroxysme, c’est-à-dire sa phase la plus violente, le 15 janvier 2022. L’explosion est alors si puissante qu’on l’entend jusqu’en Alaska, à plus de 9 000 kilomètres, et son panache de cendres et de vapeur culmine à 57 kilomètres d’altitude, plus haut qu’aucun panache volcanique jamais mesuré.

L’éruption provoque aussitôt un tsunami : les vagues dévastent des habitations sur plusieurs iles de l’archipel, puis se propagent à travers l’océan jusqu’aux côtes du Japon et du continent américain. Aux Tonga, une couche de cendres recouvre les iles, endommage les cultures et contamine les réserves d’eau potable. Sectionné, le câble sous-marin qui relie l’archipel au réseau Internet mondial n’est réparé qu’au bout de cinq semaines. Au total, les dégâts représentent plus de 18 % de la richesse que ce petit État insulaire produit en une année.

Cette catastrophe a rappelé que des volcans dissimulés sous la mer, dont l’activité reste difficile à surveiller et donc à prévoir, peuvent menacer des populations entières, en particulier celles des iles.`),
            images: [{ src: enImage(PANACHE), alt: ALT.panache }],
            legende: 'Vue de l’éruption au-dessus de l’océan Pacifique (dessin).',
            source: SOURCE_IMAGE,
        },
    ],
};

const page = (n) => SUPPORT_VOLCANS.pages[n - 1];
/** Une citation du document : elle doit se trouver mot pour mot dans la page (après la typographie). */
const citation = (n, extrait) => {
    garde(page(n).content.includes(typo(extrait)), `citation absente de la page ${n} : ${extrait}`);
    return extrait;
};
/** Ce qu'une question lit dans un dessin : le texte doit y être écrit. */
const dansLeDessin = (svg, nom, extrait) => {
    garde(svg.includes(extrait), `« ${extrait} » absent du dessin ${nom}`);
    return extrait;
};
/** Une idée qui n'est que dans certaines pages (un mauvais choix « vrai, mais d'une seule partie »). */
const seulementDans = (motif, pages, message) => {
    const ailleurs = SUPPORT_VOLCANS.pages.filter((p) => !pages.includes(p.page)).map((p) => p.content).join(' ');
    garde(pages.every((n) => motif.test(page(n).content)) && !motif.test(ailleurs), message);
};

// ─── Les questions ─────────────────────────────────────────────────────────────────────────────────

const LETTRES = ['A', 'B', 'C', 'D'];
const TEXTE = 'volcans_sous_la_mer';

/** Un QCM de la forme : quatre choix dans l'ordre fixé, la bonne réponse désignée par sa lettre. */
const qcm = (enonce, choix, lettre, elements = []) => {
    garde(choix.length === 4, `quatre choix : ${enonce}`);
    garde(LETTRES.includes(lettre), `lettre de la bonne réponse : ${lettre}`);
    return qcmForme(4, SUPPORT_VOLCANS, enonce, choix, choix[LETTRES.indexOf(lettre)], elements);
};
const question = (numero, questionNum, competence, payload) => questionForme({
    niveau: 4, texte: TEXTE, numero, questionNum, competence, payload,
});
const imageDansLEnonce = (svg, alt) => ({ type: 'image', config: { imageUrl: enImage(svg), alt, maxWidth: 'medium' } });

/**
 * Q01 (11), sens global : le rôle de la page 1 dans le document.
 * Mesure : reconnaitre la fonction d'introduction d'une partie (elle installe le cadre et annonce le sujet),
 *   sans se laisser prendre aux mots qu'elle partage avec d'autres parties.
 * Appui : page 1, « leurs profondeurs figurent toujours parmi les milieux les moins explorés de la planète »,
 *   « L'obscurité totale, un froid permanent et surtout une pression colossale », « y rendent chaque
 *   expédition particulièrement périlleuse », « se dissimulent des dizaines de milliers de volcans ».
 * Clé : B, « présenter le milieu hostile où se cachent les volcans sous-marins » (« hostile » résume
 *   l'obscurité, le froid, la pression et le péril, sans être écrit dans la page).
 * Mauvais choix :
 *   A, « raconter une expédition périlleuse » : les mots « expédition » et « périlleuse » sont dans la page, mais
 *     aucune expédition n'y est racontée (celle de 1977 est à la page 4) ;
 *   C, « expliquer comment se déroule une éruption » : la page n'en dit rien (c'est le schéma de la page 3) ;
 *   D, « décrire les animaux qui vivent dans l'obscurité totale » : « L'obscurité totale » ouvre la page, aucun
 *     animal n'y est décrit (ils sont à la page 4) ; D est le choix le plus long (relecture du 11/10 : la clé
 *     l'était).
 * Septembre : aucun item sur le rôle de la page 1 (Q03 y demandait pourquoi la région « réserve des
 *   surprises », clé « le froid a limité son exploration »).
 */
const Q01 = question(11, 'Q01', 'sens_global', (() => {
    citation(1, 'leurs profondeurs figurent toujours parmi les milieux les moins explorés de la planète');
    citation(1, 'L’obscurité totale, un froid permanent et surtout une pression colossale');
    citation(1, 'y rendent chaque expédition particulièrement périlleuse');
    citation(1, 'se dissimulent des dizaines de milliers de volcans');
    return qcm('Dans ce document, la page 1 sert surtout à…', [
        'raconter une expédition périlleuse au fond de l’océan.',
        'présenter le milieu hostile où se cachent les volcans sous-marins.',
        'expliquer comment se déroule une éruption sous-marine.',
        'décrire les animaux qui vivent dans l’obscurité totale des grands fonds.',
    ], 'B');
})());

/**
 * Q02 (12), inférence : comment des satellites repèrent des volcans qu'ils ne voient pas.
 * Mesure : relier deux phrases de la page 2 que le texte ne relie pas lui-même (les satellites mesurent la
 *   hauteur de la mer ; un volcan fait bomber la mer au-dessus de lui), en reformulant « renflement ».
 * Appui : page 2, « sans qu'aucun engin ne descende les observer », « Des satellites mesurent en effet avec une
 *   extrême précision la hauteur de la surface de l'océan », « si bien qu'à sa verticale, la mer forme un léger
 *   renflement, invisible à l'œil nu », « La plupart de ces volcans sont aujourd'hui éteints ».
 * Clé : D, « Des satellites ont détecté la bosse que forme la mer au-dessus d'eux. » (pas « repéré », le verbe
 *   de l'énoncé : seconde relecture du 11/10).
 * Mauvais choix :
 *   A, des satellites qui photographient le fond « à travers l'eau » : ils mesurent la hauteur de la surface,
 *     et le fond est plongé dans l'obscurité, sous des kilomètres d'eau (page 1) ; piège : « images
 *     satellitaires » (page 3) ;
 *   B, des hydrophones qui enregistrent leurs éruptions : la plupart de ces volcans sont éteints, et le texte
 *     attribue la découverte aux satellites (« en effet ») ; piège : les hydrophones de la page 3 ;
 *   C, des robots qui explorent le fond : « sans qu'aucun engin ne descende les observer » ; C est le choix le
 *     plus long (relecture du 11/10 : la clé l'était).
 * Septembre : aucun item sur la manière dont les volcans ont été repérés (le radar de septembre n'est pas une
 *   clé) ; les items de la page 2 portaient sur la carte (Q04) et sur un superlatif (Q05).
 */
const Q02 = question(12, 'Q02', 'inference', (() => {
    citation(2, 'sans qu’aucun engin ne descende les observer');
    citation(2, 'Des satellites mesurent en effet avec une extrême précision la hauteur de la surface de l’océan.');
    citation(2, 'si bien qu’à sa verticale, la mer forme un léger renflement, invisible à l’œil nu');
    citation(2, 'La plupart de ces volcans sont aujourd’hui éteints');
    citation(1, 'L’obscurité totale');
    citation(3, 'des images satellitaires');
    return qcm('D’après la page 2, comment des chercheurs ont-ils repéré, en 2023, plus de 19 000 volcans sous-marins ?', [
        'Des satellites ont photographié le fond de l’océan à travers l’eau.',
        'Des hydrophones ont enregistré le bruit de leurs éruptions.',
        'Des robots sous-marins ont exploré le fond de l’océan, volcan après volcan.',
        'Des satellites ont détecté la bosse que forme la mer au-dessus d’eux.',
    ], 'D');
})());

/**
 * Q03 (13), explicite : l'indice d'éruption que la page 3 ne cite pas.
 * Mesure : retrouver des informations explicites reformulées (séisme = tremblement de terre ; eau teintée = eau
 *   qui change de couleur ; radeaux de pierres ponces qui flottent = pierres qui flottent).
 * Appui : page 3, « Ils guettent ainsi une série de petits séismes ou des grondements sourds », « Une eau
 *   soudainement teintée ou des radeaux de pierres ponces, ces roches volcaniques si poreuses qu'elles
 *   flottent, peuvent également signaler une éruption. ».
 * Clé : C, « une baisse soudaine du niveau de la mer » : un phénomène marin plausible, cité nulle part dans la
 *   page (le tsunami de la page 5 est une conséquence, pas un indice). La négation est en gras (relecture du
 *   11/10).
 * Mauvais choix : A, B et D sont cités, avec d'autres mots ; l'élève qui ne reconnait pas la reformulation
 *   (« séismes », « teintée », « radeaux de pierres ponces ») croit qu'ils manquent.
 * Septembre : la page 3 de septembre citait d'autres signes, aucun item ne les demandait.
 */
const Q03 = question(13, 'Q03', 'explicite', (() => {
    citation(3, 'Ils guettent ainsi une série de petits séismes ou des grondements sourds');
    citation(3, 'Une eau soudainement teintée ou des radeaux de pierres ponces, ces roches volcaniques si poreuses qu’elles flottent, peuvent également signaler une éruption.');
    garde(!/niveau de la mer|baisse/.test(page(3).content), 'Q03, la clé ne doit pas être citée');
    return qcm('Dans la partie « Des éruptions difficiles à observer » (page 3), un seul de ces éléments <b>n’est pas</b> cité parmi les indices d’une éruption : lequel ?', [
        'une série de petits tremblements de terre',
        'des pierres qui flottent à la surface de l’eau',
        'une baisse soudaine du niveau de la mer',
        'une eau qui change brusquement de couleur',
    ], 'C');
})());

/**
 * Q04 (14), inférence : pourquoi l'éruption du schéma se voit, alors que la plupart des éruptions sous-marines
 *   échappent à l'observation directe.
 * Mesure : mettre en relation un texte et un schéma ; le texte donne la raison de l'invisibilité (la
 *   profondeur), le schéma un cratère juste sous la surface de la mer et un panache au-dessus : l'élève conclut.
 *   Le sous-titre du schéma ne dit plus « près de la surface » (relecture du 11/10 : il donnait la réponse).
 * Appui : page 3, « car elles se déroulent loin des côtes, sous des centaines, voire des milliers de mètres
 *   d'eau » ; le schéma : le sommet du volcan affleure la surface, étape 3 « Un panache de vapeur d'eau et de
 *   cendres s'élève au-dessus de la mer. » (le dessin est dans l'énoncé).
 * Clé : A, « Son cratère est si près de la surface que le panache sort de l'eau. ».
 * Mauvais choix (chacun une raison plausible que ni le texte ni le schéma ne donnent) :
 *   B, une lave « plus brulante » : le schéma dit « la lave brulante », sans rien comparer ;
 *   C, un cratère près de la surface d'où la lave coulerait sur l'eau : le début de C est celui de la clé, et
 *     seule l'étape 2 du schéma le dément (« la lave éclate en fines cendres ») ; il faut lire le schéma
 *     (seconde relecture du 11/10 : « une eau si claire qu'on voit le fond » ne tentait personne) ;
 *   D, une chambre magmatique plus vaste : elle est dessinée et nommée, jamais comparée.
 * Septembre : Q06 demandait l'information donnée par le schéma (« leurs étapes ») et Q02 l'image qui explique
 *   une éruption ; ici le schéma sert à expliquer une différence avec le texte, et la clé est autre.
 */
const Q04 = question(14, 'Q04', 'inference', (() => {
    citation(3, 'La plupart de ces éruptions échappent pourtant à toute observation directe');
    citation(3, 'car elles se déroulent loin des côtes, sous des centaines, voire des milliers de mètres d’eau');
    garde(DESSINS_VOLCANS.schema.includes(SOUS_TITRE_SCHEMA) && !SCHEMA.includes('près de la surface'),
        'Q04, le sous-titre du schéma ne doit pas donner la réponse');
    garde(page(3).images[0].src === enImage(SCHEMA), 'Q04, le schéma est celui de la page 3');
    dansLeDessin(SCHEMA, 'schéma', 'Le cratère du volcan se trouve sous la mer.');
    dansLeDessin(SCHEMA, 'schéma', 'Un panache de vapeur');
    dansLeDessin(SCHEMA, 'schéma', 'brulante');
    dansLeDessin(SCHEMA, 'schéma', 'Chambre magmatique');
    dansLeDessin(SCHEMA, 'schéma', 'la lave éclate');
    return qcm('La plupart des éruptions sous-marines échappent à toute observation directe (page 3). D’après le schéma de cette page, pourquoi l’éruption qu’il représente peut-elle, au contraire, être vue ?', [
        'Son cratère est si près de la surface que le panache sort de l’eau.',
        'Sa lave est bien plus brulante que celle des autres volcans sous-marins.',
        'Son cratère est si près de la surface que la lave coule sur l’eau.',
        'Sa chambre magmatique est plus vaste que celle des autres volcans.',
    ], 'A', [imageDansLEnonce(SCHEMA, ALT.schema)]);
})());

/**
 * Q05 (15), inférence : ce que révèle l'absence de biologiste dans l'expédition de 1977.
 * Mesure : inférer une intention à partir d'un détail (une équipe de géologues venue chercher une source d'eau
 *   chaude, « stupéfaits » devant une « oasis » de vie, sans biologiste) : personne n'attendait de vie.
 * Appui : page 4, « une expédition de géologues, de géochimistes et de géophysiciens venait précisément en
 *   chercher une », « ses passagers découvrirent, stupéfaits, une véritable « oasis » grouillant de vie »,
 *   « L'équipe ne comptait pourtant aucun biologiste. », « la vie est habituellement très rare ».
 * Clé : C, « personne ne s'attendait à trouver de la vie près des sources. ».
 * Mauvais choix :
 *   A, un refus des biologistes : une cause inventée, que rien n'appuie ;
 *   B, un sous-marin trop petit : « le petit sous-marin Alvin » est dans le texte, mais c'est l'équipe entière
 *     qui n'avait aucun biologiste, pas seulement le sous-marin ; B est le choix le plus long (relecture du
 *     11/10 : la clé l'était) ;
 *   D, des animaux déjà connus : contredit par « stupéfaits » et par « des centaines d'espèces nouvelles ».
 * Septembre : Q07 demandait pourquoi un lieu est dit « monde perdu » (clé « il est resté longtemps inconnu ») ;
 *   ici, ni le mot entre guillemets ni sa clé : un détail de l'équipe, et ce qu'il révèle de ses attentes.
 */
const Q05 = question(15, 'Q05', 'inference', (() => {
    citation(4, 'une expédition de géologues, de géochimistes et de géophysiciens venait précisément en chercher une');
    citation(4, 'ses passagers découvrirent, stupéfaits, une véritable « oasis » grouillant de vie');
    citation(4, 'L’équipe ne comptait pourtant aucun biologiste.');
    citation(4, 'la vie est habituellement très rare');
    citation(4, 'Lorsque le petit sous-marin Alvin atteignit le fond');
    citation(4, 'des centaines d’espèces nouvelles');
    return qcm('Dans la page 4, l’expédition de 1977 ne comptait aucun biologiste. Ce détail montre que…', [
        'les biologistes avaient refusé de plonger aussi profond.',
        'le sous-marin Alvin était trop petit pour emmener un biologiste.',
        'personne ne s’attendait à trouver de la vie près des sources.',
        'les animaux de ces sources étaient déjà bien connus.',
    ], 'C');
})());

/**
 * Q06 (16), inférence : où trouver des sources hydrothermales, en reliant la page 4 et la carte de la page 2.
 * Mesure : mettre en relation un texte et une carte pour en tirer une information que ni l'un ni l'autre ne
 *   donne seul (les sources se forment le long de la dorsale ; la carte montre la dorsale dans les trois
 *   océans).
 * Appui : page 4, « Le long de la dorsale, l'eau de mer s'infiltre dans les fissures du plancher océanique »,
 *   « ce sont les sources hydrothermales », « Ailleurs sur la dorsale » ; la carte de la page 2 (dans l'énoncé) :
 *   le trait rouge « Dorsale océanique » traverse l'océan Pacifique, l'océan Atlantique et l'océan Indien.
 * Clé : B, « dans les océans Pacifique, Atlantique et Indien. » (seconde relecture du 11/10 : « nommés sur la
 *   carte » reprenait l'énoncé, et la clé se repérait sans lire).
 * Mauvais choix :
 *   A, seulement près des Galápagos : c'est le lieu de la découverte de 1977, mais « Ailleurs sur la dorsale »
 *     et la carte montrent qu'il y en a d'autres ;
 *   C, au sommet des 19 000 volcans repérés en 2023 : les sources se forment le long de la dorsale (page 4), et
 *     ces volcans sont pour la plupart éteints (page 2) ; le choix ne dit plus « éteints » (relecture du
 *     11/10 : le mot écartait le choix B de Q02) ;
 *   D, au fond des plaines abyssales, loin de la dorsale : compatible avec la carte, démenti par la page 4 ;
 *     il oblige à lire la page 4 (relecture du 11/10 : « le long des côtes » tombait avec la carte seule).
 * Septembre : Q04 demandait l'information illustrée par la carte (clé : une chaine volcanique en Antarctique) ;
 *   ici la carte sert à déduire, et la clé ne porte ni sur la carte seule ni sur un continent.
 */
const Q06 = question(16, 'Q06', 'inference', (() => {
    citation(4, 'Le long de la dorsale, l’eau de mer s’infiltre dans les fissures du plancher océanique');
    citation(4, 'ce sont les sources hydrothermales');
    citation(4, 'Ailleurs sur la dorsale');
    citation(4, 'près des iles Galápagos');
    citation(2, 'plus de 19 000 volcans sous-marins jusque-là inconnus');
    citation(2, 'les plaines abyssales');
    garde(page(2).images[0].src === enImage(CARTE), 'Q06, la carte est celle de la page 2');
    // La carte écrit « OCÉAN » puis le nom de l'océan, sur deux lignes
    ['>PACIFIQUE<', '>ATLANTIQUE<', '>INDIEN<', '>Dorsale océanique<'].forEach((texte) => dansLeDessin(CARTE, 'carte', texte));
    return qcm('En reliant la page 4 à la carte de la page 2, on comprend qu’il existe des sources hydrothermales…', [
        'près des iles Galápagos, et nulle part ailleurs.',
        'dans les océans Pacifique, Atlantique et Indien.',
        'au sommet des 19 000 volcans repérés en 2023.',
        'au fond des plaines abyssales, loin de la dorsale.',
    ], 'B', [imageDansLEnonce(CARTE, ALT.carte)]);
})());

/**
 * Q07 (17), explicite : la seule affirmation vraie sur l'éruption des Tonga (page 5).
 * Mesure : retrouver des informations explicites, plus ou moins aisément repérables, en lisant chaque phrase
 *   jusqu'au bout : le 15 janvier n'est que le paroxysme, l'explosion s'entend jusqu'en Alaska, les vagues vont
 *   jusqu'au Japon et à l'Amérique.
 * Un QCM et non un tableau (relecture du 11/10) : les deux explicites de septembre sont des QCM ; un tableau de
 *   quatre lignes, noté en tout ou rien, aurait été bien plus dur et aurait faussé le taux de la famille.
 * Appui : page 5, « Entré en éruption à la fin de décembre 2021, il atteint son paroxysme, c'est-à-dire sa phase
 *   la plus violente, le 15 janvier 2022. », « qu'on l'entend jusqu'en Alaska », « puis se propagent à travers
 *   l'océan jusqu'aux côtes du Japon et du continent américain », « n'est réparé qu'au bout de cinq semaines ».
 * Clé : D, « Il a fallu cinq semaines pour rétablir la liaison Internet. ».
 * Mauvais choix :
 *   A, le début de l'éruption au 15 janvier : la date du paroxysme prise pour celle du début (« Le volcan est
 *     entré en éruption », pour ne pas confondre l'éruption et l'explosion) ;
 *   B, une explosion entendue dans l'archipel seulement : démenti par « jusqu'en Alaska » ;
 *   C, un nuage de cendres arrivé jusqu'en Alaska : c'est le bruit de l'explosion qui y arrive ; le panache
 *     monte à 57 km, la cendre retombe sur les iles (seconde relecture du 11/10 : deux choix en « n'… que »
 *     s'écartaient ensemble).
 * Septembre : la page 5 servait à l'idée principale (Q08) et à la citation illustrée (Q09) ; ici des faits.
 */
const Q07 = question(17, 'Q07', 'explicite', (() => {
    citation(5, 'Entré en éruption à la fin de décembre 2021, il atteint son paroxysme, c’est-à-dire sa phase la plus violente, le 15 janvier 2022.');
    citation(5, 'qu’on l’entend jusqu’en Alaska');
    citation(5, 'les vagues dévastent des habitations sur plusieurs iles de l’archipel, puis se propagent à travers l’océan jusqu’aux côtes du Japon et du continent américain');
    citation(5, 'n’est réparé qu’au bout de cinq semaines');
    citation(5, 'son panache de cendres et de vapeur culmine à 57 kilomètres d’altitude');
    citation(5, 'une couche de cendres recouvre les iles');
    return qcm('D’après la page 5, quelle affirmation est vraie ?', [
        'Le volcan est entré en éruption le 15 janvier 2022.',
        'L’explosion n’a été entendue que dans l’archipel des Tonga.',
        'Le nuage de cendres est arrivé jusqu’en Alaska.',
        'Il a fallu cinq semaines pour rétablir la liaison Internet.',
    ], 'D');
})());

/**
 * Q08 (18), sens global : le rôle de la dernière phrase du texte.
 * Mesure : reconnaitre une conclusion qui généralise (« des volcans dissimulés sous la mer […] peuvent menacer
 *   des populations entières ») à partir d'un exemple (l'éruption des Tonga).
 * Appui : page 5, « Cette catastrophe a rappelé que des volcans dissimulés sous la mer, dont l'activité reste
 *   difficile à surveiller et donc à prévoir, peuvent menacer des populations entières, en particulier celles
 *   des iles. ».
 * Clé : A, « tirer une leçon générale sur les volcans sous-marins. ».
 * Mauvais choix (aucun ne touche aux faits de Q07, relecture du 11/10 : le tsunami et les cinq semaines y
 *   donnaient deux réponses) :
 *   B, rassurer sur la surveillance des volcans : une visée générale, comme la clé, mais à l'opposé de la phrase
 *     (« reste difficile à surveiller ») ; seconde relecture du 11/10 : la clé ne doit plus être la seule
 *     réponse générale ;
 *   C, souligner la hauteur record du panache : c'est la fin du premier paragraphe ;
 *   D, l'ampleur des dégâts : c'est la phrase précédente (« plus de 18 % de la richesse »).
 * L'énoncé dit « la dernière phrase du texte de la page 5 » : la légende et la source du dessin viennent après.
 * Septembre : Q08 demandait ce qu'explique la page 5 (« conséquences ») ; ici la fonction d'une phrase.
 */
const Q08 = question(18, 'Q08', 'sens_global', (() => {
    citation(5, 'Cette catastrophe a rappelé que des volcans dissimulés sous la mer, dont l’activité reste difficile à surveiller et donc à prévoir, peuvent menacer des populations entières, en particulier celles des iles.');
    garde(page(5).content.trim().endsWith(typo('en particulier celles des iles.')), 'Q08, la dernière phrase du texte');
    citation(5, 'culmine à 57 kilomètres d’altitude, plus haut qu’aucun panache volcanique jamais mesuré');
    citation(5, 'plus de 18 % de la richesse');
    return qcm('La dernière phrase du texte de la page 5 sert surtout à…', [
        'tirer une leçon générale sur les volcans sous-marins.',
        'rassurer le lecteur sur la surveillance des volcans sous-marins.',
        'souligner la hauteur record du panache de cendres.',
        'donner l’ampleur des dégâts que l’éruption a causés aux Tonga.',
    ], 'A');
})());

/**
 * Q09 (19), sens global : l'idée qui revient dans presque toutes les parties.
 * Mesure : dégager le fil conducteur du document, au-delà des idées de chaque partie.
 * Appui : page 1, « les milieux les moins explorés de la planète » ; page 2, « Les fonds marins restent
 *   d'ailleurs largement méconnus », « jusque-là inconnus » ; page 3, « échappent pourtant à toute observation
 *   directe » ; page 4, « stupéfaits », « des centaines d'espèces nouvelles », « En 2023, une expédition a même
 *   découvert » ; page 5, « dont l'activité reste difficile à surveiller et donc à prévoir ».
 * Clé : A, « On connait encore mal le fond des océans et ses volcans. ».
 * Mauvais choix : trois idées générales VRAIES, mais d'une seule partie chacune (relecture du 11/10 : la clé
 *   était la seule idée générale face à trois faits) : B la menace pour les iles (page 5), C la vie sans la
 *   lumière du Soleil (page 4), D la pression qui augmente avec la profondeur (page 1).
 * Septembre : Q10 demandait la phrase qui résume le document (clé : des volcans peuvent entrer en éruption sous
 *   la glace) ; ici une idée transversale, dont la clé ne résume aucune partie.
 */
const Q09 = question(19, 'Q09', 'sens_global', (() => {
    citation(1, 'les milieux les moins explorés de la planète');
    citation(2, 'Les fonds marins restent d’ailleurs largement méconnus');
    citation(2, 'jusque-là inconnus');
    citation(3, 'échappent pourtant à toute observation directe');
    citation(4, 'des centaines d’espèces nouvelles');
    citation(4, 'En 2023, une expédition a même découvert');
    citation(5, 'dont l’activité reste difficile à surveiller et donc à prévoir');
    seulementDans(/menac/, [5], 'Q09, la menace ne doit être qu’à la page 5');
    seulementDans(/lumière/, [4], 'Q09, la lumière ne doit être qu’à la page 4');
    seulementDans(/pression/, [1], 'Q09, la pression ne doit être qu’à la page 1');
    return qcm('Quelle idée revient dans presque toutes les parties du document ?', [
        'On connait encore mal le fond des océans et ses volcans.',
        'Les volcans sous-marins menacent surtout les habitants des iles.',
        'La vie peut exister sans la lumière du Soleil.',
        'La pression de l’eau augmente avec la profondeur.',
    ], 'A');
})());

/**
 * Q10 (20), sens global : l'ordre des parties du document.
 * Mesure : saisir la progression d'ensemble (les volcans et leurs éruptions, pages 1 à 3 ; la vie autour des
 *   sources chaudes, page 4 ; un exemple récent d'éruption, page 5).
 * Quatre ordres des trois mêmes blocs (relecture du 11/10 : l'ancien énoncé, « Le document présente
 *   d'abord… », se lisait mal avec un des choix, et la clé était le choix le plus long). En connaissance de
 *   cause : le plan suit celui de septembre et se lit par les titres des pages, comme la Q11 de septembre.
 * Appui : les titres et le contenu des cinq parties.
 * Clé : B.
 * Mauvais choix : A, la vie d'abord ; C, l'exemple récent avant la vie ; D, l'exemple récent d'abord.
 * Septembre : Q11 demandait la partie utile à un exposé (clé : la page 4) ; ici la progression de l'ensemble.
 */
const Q10 = question(20, 'Q10', 'sens_global', (() => {
    citation(2, 'Au fond des océans serpente une gigantesque chaine de montagnes volcaniques');
    citation(3, 'La plupart de ces éruptions échappent pourtant à toute observation directe');
    citation(4, 'ce sont les sources hydrothermales');
    citation(5, 'Entré en éruption à la fin de décembre 2021');
    return qcm('Dans quel ordre le document présente-t-il ses informations ?', [
        'D’abord la vie autour des sources chaudes, puis les volcans et leurs éruptions, enfin un exemple récent d’éruption.',
        'D’abord les volcans et leurs éruptions, puis la vie autour des sources chaudes, enfin un exemple récent d’éruption.',
        'D’abord les volcans et leurs éruptions, puis un exemple récent d’éruption, enfin la vie autour des sources chaudes.',
        'D’abord un exemple récent d’éruption, puis les volcans et leurs éruptions, enfin la vie autour des sources chaudes.',
    ], 'B');
})());

/**
 * Q11 (21), sens global : les lecteurs auxquels le document s'adresse (sa visée).
 * Mesure : identifier le destinataire d'après la manière d'écrire : chaque mot savant est expliqué au passage.
 * Appui : « que les géologues appellent la dorsale océanique » (page 2), « les plaines abyssales, ces immenses
 *   étendues plates qui tapissent le fond des océans » (page 2), « des hydrophones, ces microphones immergés »
 *   (page 3), « des radeaux de pierres ponces, ces roches volcaniques si poreuses qu'elles flottent » (page 3),
 *   « la photosynthèse, ce processus qui transforme l'énergie du Soleil en matière vivante » (page 4), « son
 *   paroxysme, c'est-à-dire sa phase la plus violente » (page 5).
 * Clé : C, « à des lecteurs curieux, qui ne sont pas spécialistes. ».
 * Mauvais choix :
 *   A, des volcanologues : un spécialiste n'a pas besoin qu'on lui explique ces mots ; piège : « Les
 *     volcanologues connaissent depuis longtemps » (page 3), où ils sont un sujet, pas des lecteurs ;
 *   B, des navigateurs à prévenir : aucun conseil, aucune position de volcan à éviter (relecture du 11/10 :
 *     « des touristes » ne tentait personne et rappelait la « publicité touristique » de septembre) ;
 *   D, les habitants des iles : aucun conseil, la page 5 raconte un évènement passé.
 * Septembre : Q01 demandait le type de publication (clé : une publication scientifique) ; ici le lecteur visé,
 *   et la clé dit « pas spécialistes ».
 */
const Q11 = question(21, 'Q11', 'sens_global', (() => {
    citation(2, 'que les géologues appellent la dorsale océanique');
    citation(2, 'les plaines abyssales, ces immenses étendues plates qui tapissent le fond des océans');
    citation(3, 'des hydrophones, ces microphones immergés');
    citation(3, 'des radeaux de pierres ponces, ces roches volcaniques si poreuses qu’elles flottent');
    citation(4, 'la photosynthèse, ce processus qui transforme l’énergie du Soleil en matière vivante');
    citation(5, 'son paroxysme, c’est-à-dire sa phase la plus violente');
    citation(3, 'Les volcanologues connaissent depuis longtemps');
    return qcm('D’après la façon dont il est écrit, ce document s’adresse surtout…', [
        'à des volcanologues, qui connaissent déjà les termes employés.',
        'à des navigateurs, pour leur signaler les volcans à éviter.',
        'à des lecteurs curieux, qui ne sont pas spécialistes.',
        'aux habitants des iles, pour leur apprendre à fuir une éruption.',
    ], 'C');
})());

/** Les onze questions de « Volcans sous la mer », numérotées 11 à 21 dans la forme (Q01 à Q11 dans le texte). */
export const QUESTIONS_VOLCANS = [Q01, Q02, Q03, Q04, Q05, Q06, Q07, Q08, Q09, Q10, Q11];

// ─── Les gardes d'ensemble ─────────────────────────────────────────────────────────────────────────

// La numérotation et la répartition des compétences de septembre (2 / 4 / 5)
garde(QUESTIONS_VOLCANS.map((q) => q.numero).join() === '11,12,13,14,15,16,17,18,19,20,21', 'numéros 11 à 21');
garde(QUESTIONS_VOLCANS.every((q, i) => q.questionNum === `Q${String(i + 1).padStart(2, '0')}`), 'Q01 à Q11');
const repartition = QUESTIONS_VOLCANS.reduce((m, q) => ({ ...m, [q.competence]: (m[q.competence] || 0) + 1 }), {});
garde(repartition.explicite === 2 && repartition.inference === 4 && repartition.sens_global === 5,
    `répartition 2 / 4 / 5 : ${JSON.stringify(repartition)}`);

// Onze QCM, comme septembre ; les clés réparties : aucune lettre plus de trois fois (l'audit relevait une clé
// toujours en D) ; la clé n'est le choix le plus long que deux fois au plus (relecture du 11/10 : six sur dix)
const choixDe = (q) => q.payload.contenu.choix || [];
garde(QUESTIONS_VOLCANS.every((q) => choixDe(q).length === 4), 'onze QCM à quatre choix');
const cles = QUESTIONS_VOLCANS.map((q) => q.payload.contenu.bonne_reponse);
garde(LETTRES.every((l) => cles.filter((c) => c === l).length <= 3), `clés réparties : ${cles.join('')}`);
const clePlusLongue = QUESTIONS_VOLCANS.filter((q) => {
    const choix = choixDe(q);
    const cle = choix[LETTRES.indexOf(q.payload.contenu.bonne_reponse)];
    return choix.every((c) => c === cle || c.length < cle.length);
});
garde(clePlusLongue.length <= 2, `la clé est le choix le plus long : ${clePlusLongue.map((q) => q.questionNum).join(', ')}`);

// Le texte visible : orthographe rectifiée (ni î ni û, sauf sûr, mûr, dû), jamais de tiret cadratin
const textesDuDessin = (svg) => [...svg.matchAll(/>([^<]+)</g), ...svg.matchAll(/aria-label="([^"]*)"/g)].map((m) => m[1]);
const visibles = [
    SUPPORT_VOLCANS.titre,
    ...SUPPORT_VOLCANS.pages.flatMap((p) => [p.surtitre, p.title, p.content, p.legende, p.source, ...(p.images || []).map((i) => i.alt)]),
    ...[GLOBE, CARTE, SCHEMA, VERS, CRABE, PANACHE].flatMap(textesDuDessin),
    ...QUESTIONS_VOLCANS.flatMap((q) => {
        const elements = q.payload.elements_additionnels?.elements || [];
        return [q.payload.texte, ...choixDe(q), ...elements.filter((e) => e.type === 'image').map((e) => e.config.alt)];
    }),
].filter(Boolean);
const ACCENTS_GARDES = /^(sûr|sûre|sûrs|sûres|sûrement|mûr|mûre|mûrs|mûres|dû|jeûne)$/i;
const traditionnels = visibles.flatMap((t) => t.match(/[A-Za-zÀ-ÿ]*[îûÎÛ][A-Za-zÀ-ÿ]*/g) || []).filter((m) => !ACCENTS_GARDES.test(m));
garde(traditionnels.length === 0, `orthographe rectifiée : ${traditionnels.join(', ')}`);
garde(visibles.every((t) => !t.includes('—')), 'tiret cadratin dans un texte visible');
garde(SUPPORT_VOLCANS.pages.every((p, i) => p.page === i + 1 && p.title && p.images.length > 0 && /Illustration TanoEd/.test(p.source)),
    'chaque page : son titre, ses dessins et leur source');
